import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import PropTypes from "prop-types";
import { Logger } from "@base-provider";
import CustomTooltip from "../shared/customTooltip";
import getStyled from "../../../hooks/getStyled";

const { theme, styled } = getStyled();

const StyledLegend = styled("div")(({ theme }) => ({
  fontSize: 10,
  fontWeight: 700,
  maxWidth: 65,
  wordBreak: "break-word",
}));
const StyledChartWrapper = styled("div")(({ theme }) => ({
  paddingLeft: "10px",
  //added this in order to hide the first tick in xAxis
  "& .recharts-cartesian-axis.recharts-xAxis .recharts-cartesian-axis-tick:first-of-type":
    {
      display: "none",
    },
}));

const CustomizedLegend = ({ legend }) => {
  return <StyledLegend>{legend}</StyledLegend>;
};

const CustomActiveDot = (props) => {
  const { cx, cy, index, color, onActiveChange } = props;
  const SIZE = 12;

  useEffect(() => {
    let newPoint = {
      x: cx,
      y: cy,
      index: index,
    };
    onActiveChange(newPoint);
  }, [index]);

  return (
    <svg x={cx - SIZE} y={cy - SIZE} fill={color}>
      <circle cx={SIZE} cy={SIZE} r={SIZE} />
    </svg>
  );
};

const SFLineChart = ({
  data,
  xAxisKeyName = "name",
  lines,
  tooltipBody,
  legend,
  width = 600,
  height = 300,
  yAxis,
  yAxisTickFormatter,
}) => {
  const { t } = useTranslation();
  const [activePoint, setActivePoint] = useState({
    x: 0,
    y: 0,
    index: null,
  });

  Logger.of("App.Components.Shared.Line.ChartContainer").info({
    data,
    xAxisKeyName,
    lines,
    legend,
    yAxis,
  });

  const TooltipContent = ({ payload, label, active }) => {
    if (active && payload && payload.length) {
      const body = tooltipBody(label, payload);
      return <CustomTooltip>{body}</CustomTooltip>;
    }

    return null;
  };
  return (
    <StyledChartWrapper>
      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          width={width}
          height={height}
          data={data}
          margin={{
            top: 10,
            right: 25,
            left: 15,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="2 3" horizontal={false} />
          <XAxis
            type="category"
            dataKey={xAxisKeyName}
            fontSize={10}
            fontWeight={400}
            lineHeight={12}
            tickLine={false}
            tickMargin={15}
            interval={0}
            minTickGap={50}
            padding={{ right: 30 }}
          />
          {yAxis && (
            <YAxis
              type="number"
              minTickGap={5}
              fontSize={10}
              fontWeight={400}
              tickCount={yAxis.ticks.length}
              tickLine={false}
              tickMargin={15}
              lineHeight={12}
              domain={["dataMin", "dataMax"]}
              padding={{ bottom: 20 }}
              ticks={yAxis.ticks}
              tickFormatter={yAxisTickFormatter}
              {...(yAxis.tickAppendix && {
                tickFormatter: (val) =>
                  t("components.chart.appendix", {
                    value: val,
                    appendix: yAxis.tickAppendix,
                  }),
              })}
            />
          )}
          {tooltipBody && (
            <Tooltip
              cursor={{
                stroke: theme.palette.grey500.main,
                strokeWidth: 1,
                strokeDasharray: "2 3",
              }}
              content={<TooltipContent />}
              position={{ x: activePoint.x, y: activePoint.y - 28 }}
            />
          )}
          {legend && yAxis && (
            <Legend
              align="left"
              verticalAlign="bottom"
              wrapperStyle={{ bottom: 18 }}
              content={<CustomizedLegend legend={legend} />}
            />
          )}
          {lines.map((l) => (
            <Line
              key={l.dataKey}
              type={l.type}
              dataKey={l.dataKey}
              stroke={l.stroke}
              strokeWidth={l.strokeWidth ?? 3}
              activeDot={
                <CustomActiveDot color={l.dotStroke} onActiveChange={setActivePoint} />
              }
              dot={{ stroke: l.dotStroke, r: 4, strokeWidth: 8 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </StyledChartWrapper>
  );
};

SFLineChart.propTypes = {
  data: PropTypes.array,
  xAxisKeyName: PropTypes.string,
  lines: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf([
        "basis",
        "basisClosed",
        "basisOpen",
        "linear",
        "linearClosed",
        "natural",
        "monotoneX",
        "monotoneY",
        "monotone",
        "step",
        "stepBefore",
        "stepAfter",
        PropTypes.func,
      ]),
      dataKey: PropTypes.string,
      stroke: PropTypes.string,
      dotStroke: PropTypes.string,
      strokeWidth: PropTypes.number,
    })
  ),
  yAxis: PropTypes.shape({
    ticks: PropTypes.array,
    tickAppendix: PropTypes.string,
  }),
  legend: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  tooltipBody: PropTypes.any,
  yAxisTickFormatter: PropTypes.func,
};

export default SFLineChart;
